"use client"

import React, { useEffect, useRef } from "react"
import { env } from "@/lib/env"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { EntityChatTrigger, MessageComponentsProvider } from "@gorilla-works/messages"
import { useAuth } from "@/contexts/auth-context"
import { useTranslation } from "@/hooks/use-translation"
import { ClutterlessTheme } from "@/lib/message-themes"
interface MessageTriggerProps {
  workflowHubId: string
  size?: "sm" | "md" | "lg"
  className?: string
  tooltipText?: string
  zIndex?: number
  onMessageClick?: () => void
  onSidebarStateChange?: (isOpen: boolean) => void
}

export function MessageTrigger({
  workflowHubId,
  size = "sm",
  className = "",
  tooltipText,
  zIndex = 1,
  onMessageClick,
  onSidebarStateChange
}: MessageTriggerProps) {
  const { token } = useAuth()
  const { t } = useTranslation()
  const triggerRef = useRef<HTMLDivElement>(null)

  // Function to get token - can be called by the message component
  const getToken = () => {
    return token || ""
  }

  const defaultTooltipText = tooltipText || t('viewMessages') || "View Messages"

  // Handle message click
  const handleMessageClick = () => {
    if (onMessageClick) {
      onMessageClick()
    }
  }

  // Monitor sidebar state and notify parent component
  useEffect(() => {
    const checkSidebarState = () => {
      const messagePanel = document.querySelector('[class*="entity-chat-panel"]')
      const isOpen = Boolean(messagePanel && getComputedStyle(messagePanel).display !== 'none')
      
      if (onSidebarStateChange) {
        onSidebarStateChange(isOpen)
      }
      
      if (!isOpen && triggerRef.current) {
        // Find the closest table container for this specific trigger
        const tableContainer = triggerRef.current.closest('table')
        if (tableContainer) {
          // Only reset z-index for rows in this specific table
          const tableRows = tableContainer.querySelectorAll('tbody tr')
          tableRows.forEach(row => {
            if (row instanceof HTMLElement) {
              row.style.setProperty('z-index', '', 'important')
            }
          })
        }
      }
    }

    checkSidebarState()

    const observer = new MutationObserver(() => {
      checkSidebarState()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    })

    return () => observer.disconnect()
  }, [onSidebarStateChange])

  return (
    
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            ref={triggerRef}
            className="flex items-center justify-center" 
            aria-label={defaultTooltipText}
            style={{ 
              position: 'relative',
              zIndex: zIndex
            }}
            onClick={handleMessageClick}
          >
            <MessageComponentsProvider
              config={{
                apiEndpoint: env.messagesApiUrl,
                userToken: getToken
              }}
            >
              <EntityChatTrigger
                entityTypeId="WorkflowHub"
                entityId={workflowHubId}
                variant="icon"
                size={size}
                className={className}
                language="en"
                panelPosition="right"
                slideOrigin="right"
                sx={ClutterlessTheme}
                internalOnly={true}
              />
            </MessageComponentsProvider>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{defaultTooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
